import React, { useMemo, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import classNames from 'classnames';

import isEmpty from 'lodash/isEmpty';

import useResourcesData from '@/app/[locale]/auth/v1/(base)/iframe/(tabs)/signup/useResourcesData';
import Button from '@/components/Button';
import { DateOfBirthField } from '@/components/DateOfBirthField';
import NotificationError from '@/components/NotificationError';
import { TextField } from '@/components/TextField';
import { DOB_VALIDATION } from '@/constants/dobValidation';
import { createSignUpSocialMediaSchema, type SignUpSocialMediaFormData } from '@/schemas/signUpSocialMediaSchema';

import { content as defaultContent } from './helper';
import styles from './SignUpSocialMedia.module.scss';

type SignUpSocialMediaProps = {
  content?: {
    firstNameHeading?: string;
    lastnameHeading?: string;
    emailHeading?: string;
    dobHeading?: string;
    signupContinue?: string;
  };
  initialFormState?: {
    firstname?: string;
    lastname?: string;
    email?: string;
    dob?: string;
  };
  serverErrorMsg?: string[] | string;
  isErrorBoxVisible?: boolean;
  settingData?: {
    formValidations?: any;
  };
  onSubmit: (_data: SignUpSocialMediaFormData) => void;
};

const SignUpSocialMedia = ({
  content = {},
  initialFormState = {},
  serverErrorMsg = [],
  isErrorBoxVisible = false,
  settingData = {},
  onSubmit,
}: SignUpSocialMediaProps) => {
  const data = isEmpty(content) ? defaultContent : content;
  const { firstNameHeading, lastnameHeading, emailHeading, dobHeading, signupContinue } = data;
  const { formValidations } = settingData;

  const resourcesData = useResourcesData();
  const invalidTLDs = resourcesData?.invalidTLDs;

  // Create schema with dynamic validation rules
  const signUpSocialMediaSchema = useMemo(
    () => createSignUpSocialMediaSchema(formValidations, invalidTLDs, resourcesData.createAccountDobValidation),
    [formValidations, invalidTLDs, resourcesData.createAccountDobValidation],
  );

  // React Hook Form setup
  const methods = useForm<SignUpSocialMediaFormData>({
    resolver: zodResolver(signUpSocialMediaSchema),
    mode: 'all',
    defaultValues: {
      firstname: initialFormState.firstname || '',
      lastname: initialFormState.lastname || '',
      email: initialFormState.email || '',
      dob: initialFormState.dob || '',
    },
  });

  const { control, handleSubmit, reset } = methods;

  useEffect(() => {
    reset({
      firstname: initialFormState.firstname || '',
      lastname: initialFormState.lastname || '',
      email: initialFormState.email || '',
      dob: initialFormState.dob || '',
    });
  }, [initialFormState, reset]);

  const onFormSubmit = handleSubmit((formData) => {
    onSubmit(formData);
  });

  return (
    <>
      {isErrorBoxVisible && <NotificationError mainText="This page contains errors 🤦" subText={serverErrorMsg} icon />}
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...methods}>
        <form onSubmit={onFormSubmit} noValidate>
          <div className={styles.inputWrapper}>
            <TextField
              control={control}
              name="firstname"
              type="text"
              label={firstNameHeading || 'First name'}
              required
            />
          </div>
          <div className={styles.inputWrapper}>
            <TextField control={control} name="lastname" type="text" label={lastnameHeading || 'Last name'} required />
          </div>
          <div className={styles.inputWrapper}>
            <TextField control={control} name="email" type="email" label={emailHeading || 'Email'} required />
          </div>
          <div className={classNames(styles.inputWrapper, styles.dobWrapper)}>
            <label htmlFor="dob">{`${dobHeading || 'Date of birth'}`}</label>
            <DateOfBirthField
              control={control}
              name="dob"
              labelVariant="fieldGroup"
              minAge={DOB_VALIDATION.minAge.createAccount}
              maxAge={DOB_VALIDATION.maxAge}
            />
          </div>
          <Button type="submit" className={styles.submitButton} variant="primary">
            {signupContinue}
          </Button>
        </form>
      </FormProvider>
    </>
  );
};

export default SignUpSocialMedia;
